import {
  PrivateAIAssistantChatMessageSchema,
  PrivateAIAssistantChatThreadSchema,
  RTDBPath,
  type PrivateAIAssistantChatMessageJSON,
  type PrivateAIAssistantChatMessageRole,
  type PrivateAIAssistantChatThreadJSON,
  type UserId,
  type ViewModelId,
} from 'balus-common';
import { RefBuilder } from '@framework/repository';
import * as v from 'valibot';

export const DEFAULT_PRIVATE_AI_ASSISTANT_CHAT_THREAD_ID = 'default';

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function parseMessages(value: unknown): PrivateAIAssistantChatMessageJSON[] {
  if (!isRecord(value)) return [];
  const messages: PrivateAIAssistantChatMessageJSON[] = [];
  for (const [messageId, raw] of Object.entries(value)) {
    const parsed = v.safeParse(PrivateAIAssistantChatMessageSchema, raw);
    if (parsed.success && parsed.output.id === messageId) {
      messages.push(parsed.output);
    }
  }
  // createdAt 昇順で、同値の場合は id 昇順で安定化
  messages.sort((a, b) => a.createdAt - b.createdAt || a.id.localeCompare(b.id));
  return messages;
}

export class PrivateAIAssistantChatThreadRepository {
  // Firebase の push().key は時系列順にソート可能、かつ RTDB キーとして安全な文字種で生成される
  static generateThreadId(viewModelId: ViewModelId, userId: UserId): string {
    const ref = RefBuilder.ref(RTDBPath.ViewModel.privateAIAssistantChatThreadsPath(viewModelId, userId)).push();
    const key = ref.key;
    if (!key) throw new Error('Failed to generate threadId');
    return key;
  }

  static async upsertEmpty(viewModelId: ViewModelId, userId: UserId, threadId: string, title: string): Promise<void> {
    const ref = RefBuilder.ref(RTDBPath.ViewModel.privateAIAssistantChatThreadPath(viewModelId, userId, threadId));
    await ref.transaction((current) => {
      if (current !== null) return current;
      const now = Date.now();
      const thread: PrivateAIAssistantChatThreadJSON = {
        id: threadId,
        title,
        createdAt: now,
        updatedAt: now,
      };
      return thread;
    });
  }

  static async touch(viewModelId: ViewModelId, userId: UserId, threadId: string): Promise<void> {
    const ref = RefBuilder.ref(RTDBPath.ViewModel.privateAIAssistantChatThreadPath(viewModelId, userId, threadId));
    await ref.child('updatedAt').set(Date.now());
  }

  static async setTitleIfEmpty(
    viewModelId: ViewModelId,
    userId: UserId,
    threadId: string,
    title: string
  ): Promise<void> {
    const ref = RefBuilder.ref(RTDBPath.ViewModel.privateAIAssistantChatThreadPath(viewModelId, userId, threadId));
    await ref.child('title').transaction((current) => {
      // 既にタイトルが設定されている場合は上書きしない（undefined を返すとトランザクション中断）
      if (typeof current === 'string' && current !== '') return undefined;
      return title;
    });
  }

  static async getLatestThread(
    viewModelId: ViewModelId,
    userId: UserId
  ): Promise<PrivateAIAssistantChatThreadJSON | null> {
    const snapshot = await RefBuilder.ref(
      RTDBPath.ViewModel.privateAIAssistantChatThreadsPath(viewModelId, userId)
    ).get();
    const value = snapshot.val();
    if (!isRecord(value)) return null;

    let latest: PrivateAIAssistantChatThreadJSON | null = null;
    for (const [threadId, raw] of Object.entries(value)) {
      const parsed = v.safeParse(PrivateAIAssistantChatThreadSchema, raw);
      if (!parsed.success || parsed.output.id !== threadId) continue;
      if (!latest || parsed.output.updatedAt > latest.updatedAt) {
        latest = parsed.output;
      }
    }
    return latest;
  }

  static async deleteThread(viewModelId: ViewModelId, userId: UserId, threadId: string): Promise<void> {
    // スレッド本体とメッセージ群は別パスに保存されているため、ルートrefのマルチパス更新で
    // 両方を原子的に削除する。個別に remove() すると片方だけ失敗して孤児メッセージが残り得る。
    await RefBuilder.ref().update({
      [RTDBPath.ViewModel.privateAIAssistantChatThreadPath(viewModelId, userId, threadId)]: null,
      [RTDBPath.ViewModel.privateAIAssistantChatMessagesPath(viewModelId, userId, threadId)]: null,
    });
  }
}

export class PrivateAIAssistantChatMessageRepository {
  static async getMessages(
    viewModelId: ViewModelId,
    userId: UserId,
    threadId: string
  ): Promise<PrivateAIAssistantChatMessageJSON[]> {
    const snapshot = await RefBuilder.ref(
      RTDBPath.ViewModel.privateAIAssistantChatMessagesPath(viewModelId, userId, threadId)
    ).get();
    return parseMessages(snapshot.val());
  }

  static async saveMessages(
    viewModelId: ViewModelId,
    userId: UserId,
    threadId: string,
    messages: PrivateAIAssistantChatMessageJSON[]
  ): Promise<void> {
    if (messages.length === 0) return;

    const updates: Record<string, PrivateAIAssistantChatMessageJSON> = {};
    for (const message of messages) {
      const parsed = v.parse(PrivateAIAssistantChatMessageSchema, message);
      updates[parsed.id] = parsed;
    }
    await RefBuilder.ref(RTDBPath.ViewModel.privateAIAssistantChatMessagesPath(viewModelId, userId, threadId)).update(
      updates
    );
  }
}

export function buildPrivateAIAssistantChatMessage(input: {
  id: string;
  role: PrivateAIAssistantChatMessageRole;
  payload: string;
  createdAt: number;
}): PrivateAIAssistantChatMessageJSON {
  return v.parse(PrivateAIAssistantChatMessageSchema, input);
}
